if (!customElements.get('color-picker-property')) {
  /* ---------------------------------------------------------------------------
     Farb-Hilfsfunktionen (HSV <-> RGB <-> HEX)
     --------------------------------------------------------------------------- */
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  function hsvToRgb(h, s, v) {
    const c = v * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = v - c;
    let r = 0;
    let g = 0;
    let b = 0;
    if (h < 60) { r = c; g = x; }
    else if (h < 120) { r = x; g = c; }
    else if (h < 180) { g = c; b = x; }
    else if (h < 240) { g = x; b = c; }
    else if (h < 300) { r = x; b = c; }
    else { r = c; b = x; }
    return [
      Math.round((r + m) * 255),
      Math.round((g + m) * 255),
      Math.round((b + m) * 255),
    ];
  }

  function rgbToHsv(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const d = max - min;
    let h = 0;
    if (d !== 0) {
      if (max === r) h = ((g - b) / d) % 6;
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h *= 60;
      if (h < 0) h += 360;
    }
    const s = max === 0 ? 0 : d / max;
    return [h, s, max];
  }

  function rgbToHex(r, g, b) {
    return '#' + [r, g, b].map((x) => clamp(x, 0, 255).toString(16).padStart(2, '0')).join('').toUpperCase();
  }

  function hexToRgb(hex) {
    const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!match) return null;
    return [parseInt(match[1], 16), parseInt(match[2], 16), parseInt(match[3], 16)];
  }

  /* ---------------------------------------------------------------------------
     Eingabe-Formate: HEX, RGB und RAL Classic
     --------------------------------------------------------------------------- */
  const RAL_LIST = Array.isArray(window.ANTON_RAL_CLASSIC) ? window.ANTON_RAL_CLASSIC : [];

  function findRalByCode(code) {
    return RAL_LIST.find((entry) => entry.code === code) || null;
  }

  /* Freitextsuche fuer die Vorschlagsliste (Nummer oder Name) */
  function searchRal(term, limit = 6) {
    const needle = term.trim().toLowerCase().replace(/^ral\s*-?\s*/, '');
    if (!needle) return [];
    return RAL_LIST.filter(
      (entry) => entry.code.startsWith(needle) || entry.name.toLowerCase().includes(needle)
    ).slice(0, limit);
  }

  /* Naechstliegende RAL-Farbe zu einem Hex-Wert (einfache RGB-Distanz) */
  function nearestRal(hex) {
    const rgb = hexToRgb(hex);
    if (!rgb || RAL_LIST.length === 0) return null;
    let best = null;
    let bestDist = Infinity;
    RAL_LIST.forEach((entry) => {
      const c = hexToRgb(entry.hex);
      if (!c) return;
      const d =
        (c[0] - rgb[0]) * (c[0] - rgb[0]) +
        (c[1] - rgb[1]) * (c[1] - rgb[1]) +
        (c[2] - rgb[2]) * (c[2] - rgb[2]);
      if (d < bestDist) {
        bestDist = d;
        best = entry;
      }
    });
    return best;
  }

  /**
   * Erkennt das Eingabeformat und liefert { hex, label }.
   * label = null  -> es gilt der Hex-Wert (freie Farbwahl)
   * label = Text  -> verbindliche Bezeichnung (z. B. "RAL 3000 Feuerrot")
   */
  function parseColorInput(raw) {
    const value = String(raw || '').trim();
    if (!value) return null;

    // HEX: #A72920, A72920, #ABC
    const hexMatch = /^#?([a-f\d]{3}|[a-f\d]{6})$/i.exec(value);
    if (hexMatch) {
      let hex = hexMatch[1];
      if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
      return { hex: '#' + hex.toUpperCase(), label: null };
    }

    // RGB: rgb(167, 41, 32) / 167,41,32
    const rgbMatch = /^(?:rgba?\s*\()?\s*(\d{1,3})\s*[,\s]\s*(\d{1,3})\s*[,\s]\s*(\d{1,3})/i.exec(value);
    if (rgbMatch) {
      const r = clamp(parseInt(rgbMatch[1], 10), 0, 255);
      const g = clamp(parseInt(rgbMatch[2], 10), 0, 255);
      const b = clamp(parseInt(rgbMatch[3], 10), 0, 255);
      return { hex: rgbToHex(r, g, b), label: null };
    }

    // RAL: "RAL 3000", "ral3000" oder nur "3000"
    const ralMatch = /^(?:ral\s*-?\s*)?(\d{4})$/i.exec(value);
    if (ralMatch) {
      const entry = findRalByCode(ralMatch[1]);
      if (entry) return { hex: entry.hex, label: `RAL ${entry.code} ${entry.name}`, ral: entry };
    }

    // RAL per Name, z. B. "Feuerrot"
    const byName = RAL_LIST.find((entry) => entry.name.toLowerCase() === value.toLowerCase());
    if (byName) {
      return { hex: byName.hex, label: `RAL ${byName.code} ${byName.name}`, ral: byName };
    }

    return null;
  }

  /* ---------------------------------------------------------------------------
     Custom Element
     --------------------------------------------------------------------------- */
  class ColorPickerProperty extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-trigger]');
      this.triggerText = this.querySelector('[data-trigger-text]');
      this.swatch = this.querySelector('[data-swatch]');
      this.resetBtn = this.querySelector('[data-reset]');

      this.popup = this.querySelector('[data-popup]');
      this.backdrop = this.querySelector('[data-backdrop]');
      this.dialog = this.querySelector('[role="dialog"]');
      this.closeBtn = this.querySelector('[data-close]');

      this.preview = this.querySelector('[data-preview]');
      this.previewHex = this.querySelector('[data-preview-hex]');
      this.sv = this.querySelector('[data-sv]');
      this.svThumb = this.querySelector('[data-sv-thumb]');
      this.hue = this.querySelector('[data-hue]');
      this.hueThumb = this.querySelector('[data-hue-thumb]');
      this.hexText = this.querySelector('[data-hex-text]');
      this.applyBtn = this.querySelector('[data-apply]');
      this.propertyInput = this.querySelector('[data-property-input]');

      if (!this.propertyInput || !this.trigger) return;

      this.finalName = this.propertyInput.dataset.finalName;
      this.isRequired = this.dataset.required === 'true';
      this.defaultColor = this.dataset.default || '#FFFFFF';
      this.chooseLabel = this.triggerText ? this.triggerText.textContent.trim() : 'Farbton wählen';

      // HSV-Zustand
      this.h = 0;
      this.s = 0;
      this.v = 1;
      this.hasSelection = false;

      // Bezeichnung der gewaehlten Farbe (z. B. "RAL 3000 Feuerrot").
      // null = freie Farbwahl, dann gilt der Hex-Wert.
      this.colorLabel = null;
      this.selectedHex = this.dataset.default || '#FFFFFF';
      this.suggestionBox = this.querySelector('[data-suggestions]');
      this.formatHint = this.querySelector('[data-format-hint]');
      this.modeButtons = Array.from(this.querySelectorAll('[data-mode]'));
      this.inputMode = 'hex';

      if (this.isRequired) {
        // Pflichtfeld: Property-Name direkt setzen, damit der Wert mitgesendet wird
        this.propertyInput.name = this.finalName;
      }

      // Picker mit Startfarbe initialisieren
      this.setHsvFromHex(this.defaultColor);

      this.bindEvents();
      this.bindRequiredGuard();
      this.setMode('hex');
    }

    /* Pflichtfeld: Submit blockieren, solange keine Farbe gewählt wurde */
    bindRequiredGuard() {
      if (!this.isRequired) return;
      const form = this.propertyInput.form;
      if (!form) return;
      form.addEventListener(
        'submit',
        (e) => {
          if (!this.hasSelection) {
            e.preventDefault();
            e.stopPropagation();
            this.open();
          }
        },
        true
      );
    }

    bindEvents() {
      // Umschalter HEX / RGB / RAL
      this.modeButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.setMode(btn.dataset.mode));
      });

      this.trigger.addEventListener('click', () => this.open());
      this.closeBtn.addEventListener('click', () => this.close());
      this.backdrop.addEventListener('click', () => this.close());
      this.applyBtn.addEventListener('click', () => this.close());
      this.resetBtn.addEventListener('click', () => this.reset());

      this.onKeydown = (e) => {
        if (e.key === 'Escape' && !this.popup.hidden) this.close();
      };

      // Farbeingabe: HEX, RGB oder RAL
      this.hexText.addEventListener('input', (e) => {
        const value = e.target.value;
        const parsed = parseColorInput(value);

        // Tab wandert mit dem getippten Format mit
        this.syncModeFromInput(value);

        if (parsed) {
          this.hexText.setAttribute('aria-invalid', 'false');
          this.colorLabel = parsed.label;
          this.setHsvFromHex(parsed.hex);
          this.commit();
        } else {
          this.hexText.setAttribute('aria-invalid', value.trim() === '' ? 'false' : 'true');
        }

        this.renderSuggestions(value);
      });

      this.hexText.addEventListener('blur', () => {
        // Vorschlagsliste erst nach einem moeglichen Klick darauf schliessen
        setTimeout(() => {
          this.clearSuggestions();
          this.hexText.value = this.displayValue();
          this.hexText.setAttribute('aria-invalid', 'false');
        }, 150);
      });

      this.hexText.addEventListener('focus', () => this.renderSuggestions(this.hexText.value));

      // Sättigung / Helligkeit – Pointer (Maus, Touch, Stift einheitlich)
      this.bindDrag(this.sv, (clientX, clientY) => {
        const rect = this.sv.getBoundingClientRect();
        this.s = clamp((clientX - rect.left) / rect.width, 0, 1);
        this.v = clamp(1 - (clientY - rect.top) / rect.height, 0, 1);
        // Freie Farbwahl hebt eine zuvor gewaehlte RAL-Bezeichnung auf
        this.colorLabel = null;
        this.clearSuggestions();
        this.updateFromHsv();
        this.commit();
      });

      // Farbton-Regler
      this.bindDrag(this.hue, (clientX) => {
        const rect = this.hue.getBoundingClientRect();
        this.h = clamp((clientX - rect.left) / rect.width, 0, 1) * 360;
        this.colorLabel = null;
        this.clearSuggestions();
        this.updateFromHsv();
        this.commit();
      });

      // Tastatursteuerung
      this.sv.addEventListener('keydown', (e) => this.handleSvKeys(e));
      this.hue.addEventListener('keydown', (e) => this.handleHueKeys(e));
    }

    /* Pointer-Drag-Helfer: gleiches Verhalten auf allen Geräten */
    bindDrag(el, onMove) {
      const move = (e) => onMove(e.clientX, e.clientY);
      const up = (e) => {
        el.releasePointerCapture?.(e.pointerId);
        el.removeEventListener('pointermove', move);
        el.removeEventListener('pointerup', up);
        el.removeEventListener('pointercancel', up);
      };
      el.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        el.setPointerCapture?.(e.pointerId);
        move(e);
        el.addEventListener('pointermove', move);
        el.addEventListener('pointerup', up);
        el.addEventListener('pointercancel', up);
      });
    }

    handleSvKeys(e) {
      const step = e.shiftKey ? 0.1 : 0.02;
      let handled = true;
      if (e.key === 'ArrowLeft') this.s = clamp(this.s - step, 0, 1);
      else if (e.key === 'ArrowRight') this.s = clamp(this.s + step, 0, 1);
      else if (e.key === 'ArrowUp') this.v = clamp(this.v + step, 0, 1);
      else if (e.key === 'ArrowDown') this.v = clamp(this.v - step, 0, 1);
      else handled = false;
      if (handled) {
        e.preventDefault();
        this.colorLabel = null;
        this.updateFromHsv();
        this.commit();
      }
    }

    handleHueKeys(e) {
      const step = e.shiftKey ? 20 : 4;
      let handled = true;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') this.h = clamp(this.h - step, 0, 360);
      else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') this.h = clamp(this.h + step, 0, 360);
      else handled = false;
      if (handled) {
        e.preventDefault();
        this.colorLabel = null;
        this.updateFromHsv();
        this.commit();
      }
    }

    /* ---- Zustand / Darstellung ---- */
    currentHex() {
      const [r, g, b] = hsvToRgb(this.h, this.s, this.v);
      return rgbToHex(r, g, b);
    }

    setHsvFromHex(hex) {
      const rgb = hexToRgb(hex);
      if (!rgb) return;
      [this.h, this.s, this.v] = rgbToHsv(rgb[0], rgb[1], rgb[2]);
      this.updateFromHsv();
    }

    /* Aktualisiert alle Picker-Elemente anhand des HSV-Zustands */
    updateFromHsv() {
      const hex = this.currentHex();

      // SV-Fläche bekommt den reinen Farbton als Hintergrund
      this.sv.style.backgroundColor = `hsl(${this.h}, 100%, 50%)`;

      // Thumb-Positionen
      this.svThumb.style.left = `${this.s * 100}%`;
      this.svThumb.style.top = `${(1 - this.v) * 100}%`;
      this.hueThumb.style.left = `${(this.h / 360) * 100}%`;
      this.hueThumb.style.backgroundColor = `hsl(${this.h}, 100%, 50%)`;

      // Vorschau + Eingabefeld
      this.preview.style.backgroundColor = hex;
      this.preview.style.backgroundImage = 'none';
      // In der Vorschau immer Bezeichnung (falls RAL) und Hex zeigen
      this.previewHex.textContent = this.colorLabel ? this.colorLabel + ' · ' + hex : hex;
      if (document.activeElement !== this.hexText) {
        this.hexText.value = this.displayValue();
      }
      this.updateFormatHint();
      this.hue.setAttribute('aria-valuenow', Math.round(this.h));
    }

    /* Erkennt das Format einer Eingabe, um den Tab mitwandern zu lassen */
    detectFormat(value) {
      const v = String(value || '').trim();
      if (!v) return null;
      if (/^#?([a-f\d]{3}|[a-f\d]{6})$/i.test(v)) return 'hex';
      if (/rgba?\s*\(/i.test(v) || /^\d{1,3}\s*[,\s]\s*\d{1,3}\s*[,\s]\s*\d{1,3}/.test(v)) return 'rgb';
      if (/^(ral\b|\d{4}$)/i.test(v)) return 'ral';
      if (RAL_LIST.some((e) => e.name.toLowerCase().startsWith(v.toLowerCase()))) return 'ral';
      return null;
    }

    /* Tab an die Eingabe angleichen, ohne den getippten Text zu ueberschreiben */
    syncModeFromInput(value) {
      const detected = this.detectFormat(value);
      if (!detected || detected === this.inputMode) return;
      this.inputMode = detected;
      this.modeButtons.forEach((btn) => {
        const active = btn.dataset.mode === detected;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      this.updateFormatHint();
    }

    /* ---- Eingabeformat (HEX / RGB / RAL) ---- */
    setMode(mode) {
      this.inputMode = mode;
      this.modeButtons.forEach((btn) => {
        const active = btn.dataset.mode === mode;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      this.hexText.placeholder =
        mode === 'rgb' ? 'rgb(167, 41, 32)' : mode === 'ral' ? 'RAL 3000 oder Feuerrot' : '#RRGGBB';
      this.hexText.value = this.displayValue();
      this.hexText.setAttribute('aria-invalid', 'false');
      this.clearSuggestions();
      this.updateFormatHint();

      // Property an das gewaehlte Format angleichen
      if (this.hasSelection) this.commit();
    }

    /* Wert im aktuell gewaehlten Format */
    displayValue() {
      const hex = this.currentHex();
      if (this.inputMode === 'ral') return this.colorLabel || '';
      if (this.inputMode === 'rgb') {
        const [r, g, b] = hsvToRgb(this.h, this.s, this.v);
        return `rgb(${r}, ${g}, ${b})`;
      }
      return hex;
    }

    /* Hinweiszeile unter dem Feld */
    updateFormatHint() {
      if (!this.formatHint) return;

      if (this.inputMode === 'ral' && !this.colorLabel) {
        const near = nearestRal(this.currentHex());
        if (near) {
          this.formatHint.innerHTML = '';
          const text = document.createTextNode('Kein exakter RAL-Ton. Nächstliegend: ');
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'color-picker-property__nearest';
          btn.textContent = `RAL ${near.code} ${near.name}`;
          btn.addEventListener('click', () => {
            this.colorLabel = `RAL ${near.code} ${near.name}`;
            this.setHsvFromHex(near.hex);
            this.commit();
            this.hexText.value = this.displayValue();
            this.updateFormatHint();
          });
          this.formatHint.appendChild(text);
          this.formatHint.appendChild(btn);
          return;
        }
      }

      const examples = {
        hex: 'Format: <strong>#A72920</strong> oder <strong>#ABC</strong>',
        rgb: 'Format: <strong>rgb(167, 41, 32)</strong> oder <strong>167, 41, 32</strong>',
        ral: 'Format: <strong>RAL 3000</strong>, <strong>3000</strong> oder <strong>Feuerrot</strong>',
      };
      this.formatHint.innerHTML = examples[this.inputMode] || '';
    }

    /* Übernimmt die aktuelle Farbe in Trigger + Property-Feld */
    commit() {
      const hex = this.currentHex();
      this.hasSelection = true;
      this.selectedHex = hex;

      // Uebermittelter Wert: RAL-Bezeichnung hat Vorrang (verbindlich fuer die
      // Produktion), sonst das gewaehlte Format. Nie leer.
      const submitted = this.colorLabel || this.displayValue() || hex;

      // Trigger-Vorschau
      this.swatch.style.backgroundColor = hex;
      this.swatch.dataset.hasColor = 'true';
      this.triggerText.textContent = submitted;

      this.propertyInput.name = this.finalName;
      this.propertyInput.value = submitted;

      this.resetBtn.hidden = false;
    }

    reset() {
      this.hasSelection = false;
      this.colorLabel = null;
      this.selectedHex = this.defaultColor;
      this.clearSuggestions();
      if (!this.isRequired) {
        this.propertyInput.name = '';
      }
      this.propertyInput.value = '';

      this.swatch.dataset.hasColor = 'false';
      this.swatch.style.backgroundColor = '';
      this.triggerText.textContent = this.chooseLabel;
      this.resetBtn.hidden = true;

      // Picker auf Startfarbe zurücksetzen
      this.setHsvFromHex(this.defaultColor);
    }

    isValidHex(value) {
      return /^#[A-Fa-f0-9]{6}$/.test(value);
    }

    /* ---- RAL-Vorschlaege ---- */
    renderSuggestions(term) {
      if (!this.suggestionBox) return;

      // Vorschlaege nur im RAL-Modus
      if (this.inputMode !== 'ral') {
        this.clearSuggestions();
        return;
      }

      // Nur vorschlagen, solange nicht bereits exakt getroffen wurde
      const matches = searchRal(term);
      if (matches.length === 0 || parseColorInput(term)) {
        this.clearSuggestions();
        return;
      }

      this.suggestionBox.innerHTML = '';
      matches.forEach((entry) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'color-picker-property__suggestion';
        item.innerHTML =
          '<span class="color-picker-property__suggestion-swatch" style="background-color:' +
          entry.hex +
          '"></span><span>RAL ' +
          entry.code +
          ' · ' +
          entry.name +
          '</span>';
        item.addEventListener('click', () => {
          this.colorLabel = 'RAL ' + entry.code + ' ' + entry.name;
          this.setHsvFromHex(entry.hex);
          this.commit();
          this.hexText.value = this.displayValue();
          this.hexText.setAttribute('aria-invalid', 'false');
          this.clearSuggestions();
        });
        this.suggestionBox.appendChild(item);
      });
      this.suggestionBox.hidden = false;
    }

    clearSuggestions() {
      if (!this.suggestionBox) return;
      this.suggestionBox.innerHTML = '';
      this.suggestionBox.hidden = true;
    }

    /* ---- Popup ---- */
    open() {
      // Picker auf den aktuell gewählten (oder Standard-) Wert setzen.
      // Achtung: propertyInput kann eine RAL-Bezeichnung enthalten, daher
      // wird der zugehoerige Hex-Wert separat gehalten.
      const startHex = this.hasSelection ? this.selectedHex : this.defaultColor;
      this.setHsvFromHex(startHex);
      this.clearSuggestions();

      this.popup.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('color-picker-open');
      document.addEventListener('keydown', this.onKeydown);
      // Fokus für Tastaturnutzer in den Dialog setzen
      requestAnimationFrame(() => this.sv.focus());
    }

    close() {
      this.popup.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('color-picker-open');
      document.removeEventListener('keydown', this.onKeydown);
      this.trigger.focus();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      document.body.classList.remove('color-picker-open');
    }
  }

  customElements.define('color-picker-property', ColorPickerProperty);
}
